import { useCallback, useMemo, useState } from 'react';
import { projectForecast } from '../model/projectForecast';
import { applySensitivity } from '../model/sensitivity';
import { PAID_BUDGET_MODE_CUSTOM } from '../model/channels';

const LEVERS = [
  { id: 'cac', label: 'CAC', shockKey: 'cacPct' },
  { id: 'marketing', label: 'Marketing', shockKey: 'marketingPct' },
  { id: 'churn', label: 'Churn', shockKey: 'churnPct' },
];

function pickRunway(projection, band) {
  if (band === 'p10') return projection.runwayP10 ?? projection.runwayMonths;
  if (band === 'p90') return projection.runwayP90 ?? projection.runwayMonths;
  return projection.runwayMonths;
}

function horizonMrr(projection, band = 'p50') {
  const months = projection?.months || [];
  if (!months.length) return 0;
  const last = months[months.length - 1];
  if (band === 'p10') return last.mrrP10 ?? last.endMrr ?? 0;
  if (band === 'p90') return last.mrrP90 ?? last.endMrr ?? 0;
  return last.mrrP50 ?? last.endMrr ?? 0;
}

function projectPack(pack, monteCarloEnabled) {
  return projectForecast({
    activeScenario: 'base',
    packs: { base: pack },
    monteCarlo: { enabled: Boolean(monteCarloEnabled), runs: 80, seed: 20260812 },
  });
}

function formatDelta(n, { money = false, suffix = '' } = {}) {
  if (n == null || !Number.isFinite(n)) return '—';
  const sign = n > 0 ? '+' : '';
  if (money) return `${sign}$${Math.round(n).toLocaleString()}`;
  return `${sign}${Math.round(n)}${suffix}`;
}

function shocksFor(leverId, direction) {
  const pct = direction > 0 ? 20 : -20;
  if (leverId === 'cac') return { cacPct: pct };
  if (leverId === 'marketing') return { marketingPct: pct };
  if (leverId === 'churn') return { churnPct: pct };
  return {};
}

function buildPatch(before, after) {
  const patch = {};
  const marketingBudgetChanged =
    after.assumptions?.marketingBudgetMo !== before.assumptions?.marketingBudgetMo;
  const marketingPhasesChanged =
    JSON.stringify(after.assumptions?.marketingPhases || []) !==
    JSON.stringify(before.assumptions?.marketingPhases || []);
  if (marketingBudgetChanged || marketingPhasesChanged) {
    patch.assumptions = {
      ...(marketingBudgetChanged
        ? { marketingBudgetMo: after.assumptions.marketingBudgetMo }
        : {}),
      ...(marketingPhasesChanged
        ? { marketingPhases: after.assumptions.marketingPhases }
        : {}),
    };
    patch.channels = {
      paid: {
        ...(before.channels?.paid || {}),
        budgetMo:
          after.channels?.paid?.budgetMode === PAID_BUDGET_MODE_CUSTOM
            ? after.channels.paid.budgetMo
            : after.assumptions.marketingBudgetMo,
      },
    };
  }
  if (after.channels?.paid?.cac !== before.channels?.paid?.cac) {
    patch.channels = {
      ...(patch.channels || {}),
      paid: {
        ...(before.channels?.paid || {}),
        ...(patch.channels?.paid || {}),
        cac: after.channels.paid.cac,
      },
    };
  }
  if (JSON.stringify(after.cohorts?.retention) !== JSON.stringify(before.cohorts?.retention)) {
    patch.cohorts = { retention: after.cohorts.retention };
  }
  return patch;
}

/**
 * One-click ±20% sensitivity strip for Results.
 */
export default function SensitivityStrip({
  pack,
  band = 'p50',
  onPreview,
  onApply,
}) {
  const [activeLever, setActiveLever] = useState(null);
  const [direction, setDirection] = useState(1);

  const baseline = useMemo(
    () => projectPack(pack, false),
    [pack]
  );

  const preview = useMemo(() => {
    if (!activeLever) return null;
    const shocks = shocksFor(activeLever, direction);
    const adjustedPack = applySensitivity(pack, shocks);
    const adjusted = projectPack(adjustedPack, false);
    return {
      lever: activeLever,
      direction,
      deltaHorizonMrr: horizonMrr(adjusted, band) - horizonMrr(baseline, band),
      deltaRunway: pickRunway(adjusted, band) - pickRunway(baseline, band),
      adjustedPack,
      patch: buildPatch(pack, adjustedPack),
    };
  }, [activeLever, direction, pack, baseline, band]);

  const handleSelect = useCallback(
    (leverId, dir) => {
      setActiveLever(leverId);
      setDirection(dir);
      const shocks = shocksFor(leverId, dir);
      const adjustedPack = applySensitivity(pack, shocks);
      const adjusted = projectPack(adjustedPack, false);
      onPreview?.({
        lever: leverId,
        direction: dir,
        deltaHorizonMrr: horizonMrr(adjusted, band) - horizonMrr(baseline, band),
        deltaRunway: pickRunway(adjusted, band) - pickRunway(baseline, band),
      });
    },
    [pack, baseline, band, onPreview]
  );

  const handleApply = () => {
    if (!preview?.patch || !Object.keys(preview.patch).length) return;
    const leverLabel = LEVERS.find((l) => l.id === preview.lever)?.label || preview.lever;
    const dirLabel = preview.direction > 0 ? '+20%' : '−20%';
    const ok = window.confirm(
      `Apply ${leverLabel} ${dirLabel} to the active scenario pack?`
    );
    if (!ok) return;
    onApply?.(preview.patch);
    setActiveLever(null);
    onPreview?.(null);
  };

  return (
    <section className="chart-card economics-panel forecast-sensitivity-strip">
      <h2>Sensitivity (±20%)</h2>
      <p className="chart-hint">
        Deterministic preview vs baseline (P50 reference). Apply writes into the active pack after
        confirm; Monte Carlo bands refresh after the patch is applied.
      </p>

      <div className="economics-preset-row">
        {LEVERS.map((lever) => (
          <span key={lever.id} className="forecast-sensitivity-lever">
            <span className="economics-muted">{lever.label}</span>
            <button
              type="button"
              className="secondary-button"
              onClick={() => handleSelect(lever.id, -1)}
            >
              −20%
            </button>
            <button
              type="button"
              className="secondary-button"
              onClick={() => handleSelect(lever.id, 1)}
            >
              +20%
            </button>
          </span>
        ))}
      </div>

      {preview && (
        <div className="kpi-grid">
          <article className="kpi-card">
            <p className="kpi-label">Δ horizon MRR</p>
            <p className="kpi-value">{formatDelta(preview.deltaHorizonMrr, { money: true })}</p>
          </article>
          <article className="kpi-card">
            <p className="kpi-label">Δ runway ({band.toUpperCase()})</p>
            <p className="kpi-value">{formatDelta(preview.deltaRunway, { suffix: ' mo' })}</p>
          </article>
        </div>
      )}

      {preview && onApply && (
        <div className="economics-preset-row">
          <button type="button" className="primary-button" onClick={handleApply}>
            Apply as scenario patch
          </button>
        </div>
      )}
    </section>
  );
}
